import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import BackofficeLayout from "@/components/layouts/BackofficeLayout";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { ArrowLeft, Loader2 } from "lucide-react";
import { propertyApi, associationApi } from "@/services/api";
import { Property } from "@/models";
import PropertyFormContent from "@/components/properties/PropertyFormContent";
import { UnderlinedTabs, UnderlinedTabsList, UnderlinedTabsTrigger, UnderlinedTabsContent } from "@/components/ui/underlined-tabs";
import { FormProvider, useForm } from "react-hook-form";
import { AssociationFormValues } from "@/types/association";
import { GeneralInformationTab } from "@/components/associations/edit-tabs/GeneralInformationTab";
import { BillingTab } from "@/components/associations/edit-tabs/BillingTab";
import { DefaultPropertyAddressTab } from "@/components/associations/edit-tabs/DefaultPropertyAddressTab";
import { AssociationPropertiesTab } from "@/components/associations/AssociationPropertiesTab";
import { extractDataArray } from "@/utils/apiUtils";

const PropertyForm = () => {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const isNewProperty = !id || id === "add" || id === "new";
  const propertyId = isNewProperty ? undefined : parseInt(id || "0");
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  
  // Get query parameters for context
  const searchParams = new URLSearchParams(location.search);
  const associationIdFromUrl = searchParams.get('association_id');
  const returnToContext = searchParams.get('return_to');
  const initialAssociationId = associationIdFromUrl ? parseInt(associationIdFromUrl) : undefined;
  
  // Flag to determine if we're in an association context
  const isAssociationContext = returnToContext === 'association' && initialAssociationId;

  const [property, setProperty] = useState<Partial<Property>>({
    address: "",
    association_id: initialAssociationId,
    city: "",
    state: "",
    zip_code: "",
    suite_unit: "",
    latitude: undefined,
    longitude: undefined
  });

  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<string>("properties");

  // Setup form for association context
  const associationForm = useForm<AssociationFormValues>({
    defaultValues: {
      name: "",
      association_type: "Condominium",
      email: "",
      phone: "",
      ein: "",
      tax_classification: null,
      billing_address: "",
      billing_unit: "",
      billing_city: "",
      billing_state: "",
      billing_zip: "",
      default_property_address: "",
      default_property_city: "",
      default_property_state: "",
      default_property_zip: "",
      default_property_latitude: undefined,
      default_property_longitude: undefined,
      bank_account: "",
      routing: "",
      parent_association_id: null,
      pricing_table_id: null,
      document_groups: [],
    }
  });

  const { data: propertyData, isLoading: isLoadingProperty, error: propertyError } = useQuery({
    queryKey: ['property', propertyId],
    queryFn: () => propertyId ? propertyApi.getById(propertyId) : null,
    enabled: !!propertyId && !isNewProperty,
    meta: {
      onError: (error: Error) => {
        toast({
          title: "Error loading property",
          description: error.message,
          variant: "destructive"
        });
      }
    }
  });

  const { data: associationsResponse = [], isLoading: isLoadingAssociations, error: associationsError } = useQuery({
    queryKey: ['associations'],
    queryFn: () => associationApi.list(),
    enabled: !isAssociationContext, // Only fetch associations if not in association context
    meta: {
      onError: (error: Error) => {
        toast({
          title: "Error loading associations",
          description: error.message,
          variant: "destructive"
        });
      }
    }
  });

  // Extract the associations array
  const associations = extractDataArray(associationsResponse);

  // If in association context, fetch only the specific association
  const { data: singleAssociation, isLoading: isLoadingSingleAssociation } = useQuery({
    queryKey: ['association', initialAssociationId],
    queryFn: () => initialAssociationId ? associationApi.getById(initialAssociationId) : null,
    enabled: !!isAssociationContext,
    meta: {
      onError: (error: Error) => {
        toast({
          title: "Error loading association",
          description: error.message,
          variant: "destructive"
        });
      }
    }
  });

  useEffect(() => {
    if (propertyData && !isLoadingProperty) {
      setProperty({
        id: propertyData.id,
        address: propertyData.address,
        association_id: propertyData.association_id,
        city: propertyData.city,
        state: propertyData.state,
        zip_code: propertyData.zip_code,
        suite_unit: propertyData.suite_unit,
        latitude: propertyData.latitude,
        longitude: propertyData.longitude
      });
    }
  }, [propertyData, isLoadingProperty]);

  useEffect(() => {
    if (initialAssociationId && isNewProperty) {
      if (isAssociationContext && singleAssociation) {
        // When in association context, use the single association data
        setProperty(prev => ({
          ...prev,
          association_id: initialAssociationId,
          address: singleAssociation.default_property_address || "",
          city: singleAssociation.default_property_city || "",
          state: singleAssociation.default_property_state || "",
          zip_code: singleAssociation.default_property_zip || ""
        }));
      } else if (associations && associations.length > 0) {
        // When not in association context, use the associations array
        const selectedAssociation = associations.find(a => a.id === initialAssociationId);
        if (selectedAssociation) {
          setProperty(prev => ({
            ...prev,
            association_id: initialAssociationId,
            address: selectedAssociation.default_property_address || "",
            city: selectedAssociation.default_property_city || "",
            state: selectedAssociation.default_property_state || "",
            zip_code: selectedAssociation.default_property_zip || ""
          }));
        }
      }
    }
  }, [associations, singleAssociation, initialAssociationId, isNewProperty, isAssociationContext]);

  // Populating the association form when in association context
  useEffect(() => {
    if (isAssociationContext && singleAssociation) {
      associationForm.reset({
        management_company_id: singleAssociation.management_company_id,
        name: singleAssociation.name,
        association_type: singleAssociation.association_type,
        email: singleAssociation.email || "",
        phone: singleAssociation.phone || "",
        ein: singleAssociation.ein || "",
        tax_classification: singleAssociation.tax_classification || null,
        billing_address: singleAssociation.billing_address || "",
        billing_unit: singleAssociation.billing_unit || "",
        billing_city: singleAssociation.billing_city || "",
        billing_state: singleAssociation.billing_state || "",
        billing_zip: singleAssociation.billing_zip || "",
        default_property_address: singleAssociation.default_property_address || "",
        default_property_city: singleAssociation.default_property_city || "",
        default_property_state: singleAssociation.default_property_state || "",
        default_property_zip: singleAssociation.default_property_zip || "",
        default_property_latitude: singleAssociation.default_property_latitude || undefined,
        default_property_longitude: singleAssociation.default_property_longitude || undefined,
        bank_account: singleAssociation.bank_account || "",
        routing: singleAssociation.routing || "",
        parent_association_id: singleAssociation.parent_association_id || null,
        pricing_table_id: singleAssociation.pricing_table_id || null,
        document_groups: singleAssociation.document_groups || [],
      });
    }
  }, [singleAssociation, isAssociationContext, associationForm]);

  const createMutation = useMutation({
    mutationFn: (data: Partial<Property>) => propertyApi.create(data as Omit<Property, 'id' | 'created_at' | 'updated_at'>),
    onSuccess: () => {
      toast({
        title: "Property created",
        description: "The property has been created successfully."
      });
      queryClient.invalidateQueries({ queryKey: ['properties'] });
      handleNavigateBack();
      setLoading(false);
    },
    onError: (error: any) => {
      toast({
        title: "Error creating property",
        description: error.message || "Failed to create property",
        variant: "destructive"
      });
      setLoading(false);
    }
  });

  const updateMutation = useMutation({
    mutationFn: (data: Partial<Property>) => propertyApi.update(propertyId!, data),
    onSuccess: () => {
      toast({
        title: "Property updated",
        description: "The property has been updated successfully."
      });
      queryClient.invalidateQueries({ queryKey: ['properties'] });
      queryClient.invalidateQueries({ queryKey: ['property', propertyId] });
      handleNavigateBack();
      setLoading(false);
    },
    onError: (error: any) => {
      toast({
        title: "Error updating property",
        description: error.message || "Failed to update property",
        variant: "destructive"
      });
      setLoading(false);
    }
  });

  const handleNavigateBack = () => {
    if (returnToContext === 'association' && initialAssociationId) {
      // Navigate back to the association's properties tab
      navigate(`/backoffice/associations/${initialAssociationId}/edit`, { state: { activeTab: 'properties' } });
    } else {
      // Default fallback
      navigate('/backoffice/properties');
    }
  };

  const handleSaveProperty = async () => {
    if (!property.address || !property.association_id) {
      toast({
        title: "Validation Error",
        description: "Please fill in all required fields.",
        variant: "destructive"
      });
      return;
    }

    setLoading(true);
    if (isNewProperty) {
      createMutation.mutate({
        address: property.address!,
        association_id: property.association_id!,
        city: property.city,
        state: property.state,
        zip_code: property.zip_code,
        suite_unit: property.suite_unit,
        latitude: property.latitude,
        longitude: property.longitude
      });
    } else {
      updateMutation.mutate({
        address: property.address!,
        association_id: property.association_id!,
        city: property.city,
        state: property.state,
        zip_code: property.zip_code,
        suite_unit: property.suite_unit,
        latitude: property.latitude,
        longitude: property.longitude
      });
    }
  };

  const handleSelectDocumentGroup = () => {};
  const handleRemoveDocumentGroup = () => {};
  const handleBillingAddressSelect = () => {};
  const handleDefaultPropertyAddressSelect = () => {};
  const handleLocationSelect = () => {};
  const getPricingTableName = () => "Unknown";

  if (propertyError || associationsError) {
    return (
      <BackofficeLayout>
        <div className="flex flex-col items-center justify-center h-[60vh]">
          <h2 className="text-2xl font-bold mb-4">Error Loading Data</h2>
          <p className="text-gray-500 mb-4">
            There was an error loading the property or association data.
          </p>
          <Button onClick={handleNavigateBack}>
            <ArrowLeft className="mr-2 h-4 w-4" /> Back
          </Button>
        </div>
      </BackofficeLayout>
    );
  }

  // Determine which associations array to use for the PropertyFormContent
  const associationsToUse = isAssociationContext && singleAssociation 
    ? [singleAssociation] 
    : associations || [];

  const isLoadingAnyAssociations = isAssociationContext 
    ? isLoadingSingleAssociation 
    : isLoadingAssociations;

  // Non-association context UI
  if (!isAssociationContext) {
    return (
      <BackofficeLayout>
        <div className="space-y-6">
          <div className="flex items-center gap-2">
            <Button variant="outline" size="icon" onClick={handleNavigateBack}>
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <h1 className="text-3xl font-bold tracking-tight">
              {isNewProperty ? "New Property" : "Edit Property"}
            </h1>
          </div>

          <PropertyFormContent
            property={property}
            setProperty={setProperty}
            associations={associationsToUse}
            isLoading={loading || isLoadingProperty || isLoadingAnyAssociations}
            onSave={handleSaveProperty}
            showAssociationField={!isAssociationContext}
          />
        </div>
      </BackofficeLayout>
    );
  }

  // Association context UI with tabs
  return (
    <BackofficeLayout>
      <div className="container mx-auto py-6 space-y-6">
        <div className="flex justify-between items-center">
          <h1 className="text-3xl font-bold">
            {singleAssociation ? singleAssociation.name : "Edit Association"}
          </h1>
          
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              onClick={() => navigate(`/backoffice/associations`)}
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Associations
            </Button>
          </div>
        </div>
        
        <FormProvider {...associationForm}>
          <form>
            <UnderlinedTabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
              <UnderlinedTabsList>
                <UnderlinedTabsTrigger value="general">General Information</UnderlinedTabsTrigger>
                <UnderlinedTabsTrigger value="billing">Billing</UnderlinedTabsTrigger>
                <UnderlinedTabsTrigger value="location">Default Property Address</UnderlinedTabsTrigger>
                <UnderlinedTabsTrigger value="properties">Properties</UnderlinedTabsTrigger>
              </UnderlinedTabsList>
              
              <UnderlinedTabsContent value="general" className="space-y-4">
                <GeneralInformationTab 
                  pricingTables={[]}
                  onSelectDocumentGroup={handleSelectDocumentGroup}
                  onRemoveDocumentGroup={handleRemoveDocumentGroup}
                  getPricingTableName={getPricingTableName}
                />
              </UnderlinedTabsContent>
              
              <UnderlinedTabsContent value="billing" className="space-y-4">
                <BillingTab handleBillingAddressSelect={handleBillingAddressSelect} />
              </UnderlinedTabsContent>
              
              <UnderlinedTabsContent value="location" className="space-y-4">
                <DefaultPropertyAddressTab 
                  handleDefaultPropertyAddressSelect={handleDefaultPropertyAddressSelect}
                  handleLocationSelect={handleLocationSelect}
                />
              </UnderlinedTabsContent>
              
              <UnderlinedTabsContent value="properties" className="space-y-4">
                {/* Property edit form embedded directly in the properties tab */}
                <div className="mb-8">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-semibold">
                      {isNewProperty ? "Add New Property" : "Edit Property"}
                    </h3>
                    <Button variant="outline" size="sm" onClick={handleNavigateBack}>
                      <ArrowLeft className="h-4 w-4 mr-2" />
                      Back to Properties
                    </Button>
                  </div>
                  <PropertyFormContent
                    property={property}
                    setProperty={setProperty}
                    associations={associationsToUse}
                    isLoading={loading || isLoadingProperty || isLoadingAnyAssociations}
                    onSave={handleSaveProperty}
                    showAssociationField={false}
                    showSuiteUnitFirst={true}
                  />
                </div>
              </UnderlinedTabsContent>
            </UnderlinedTabs>
          </form>
        </FormProvider>
      </div>
    </BackofficeLayout>
  );
};

export default PropertyForm;
